import { Chip, ChipBackgroundColor } from "@amzn/stencil-react-components/chip";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import {
  ProgressTracker,
  ProgressTrackerStep,
  ProgressTrackerStepStatus,
} from "@amzn/stencil-react-components/progress-tracker";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";
import { H2, H3, Text } from "@amzn/stencil-react-components/text";
import { IconCross } from "@amzn/stencil-react-icons";
import IconExternalLinkSmall from "@amzn/stencil-react-icons/icons/icon-external-link-small";
import Button, { ButtonVariant } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Link } from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services";
import { CatalogicalService } from "lib/services/catalogical-service";
import { CSRFService } from "lib/services/csrf-service";
import { UserInformation } from "lib/services/user-information-service";
import React, { FC, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { UseCaseImpactPanel } from "../components/UseCaseImpactPanel";
import { UseCaseMetaBar } from "../components/UseCaseMetaBar";
import { UseCaseSteps } from "../components/UseCaseSteps";
import { useToolNames } from "../hooks/useToolNames";
import { useUseCaseMetadata } from "../hooks/useUseCaseMetadata";
import { buildUseCasePayload } from "./build-use-case-payload";
import { CONTRIBUTE_STEPS } from "./contribute-constants";
import { UseCaseFormData } from "./UseCaseContributePage";

interface ReviewAndSubmitStepProps {
  formData: UseCaseFormData;
  onBack: () => void;
  onSubmitted: () => void;
  draftId: string | null;
  savedItem: Record<string, any> | null;
  onDraftSaved?: (id: string, item: Record<string, any>) => void;
}

export const ReviewAndSubmitStep: FC<ReviewAndSubmitStepProps> = ({
  formData,
  onBack,
  onSubmitted,
  draftId,
  savedItem,
  onDraftSaved,
}) => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const toolNames = useToolNames(formData.toolsUsed);
  const { categories: categoryOptions } = useUseCaseMetadata();
  const categoryLabel = (val: string) => categoryOptions.find((o) => o.value === val)?.label || val;

  const contributorAlias = UserPermissions.current.username || "unknown";
  const userInfo = UserInformation.current.getCachedAmazonianInformation();
  const contributorDisplayName = userInfo
    ? `${userInfo.basicInfo?.firstName || ""} ${userInfo.basicInfo?.lastName || ""}`.trim()
    : "";
  const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const impactIndicators = [
    ...formData.impactIndicators.filter((i) => i !== "OTHER"),
    ...(formData.otherImpactIndicator ? [formData.otherImpactIndicator] : []),
  ];

  const buildPayload = (status: "DRAFT" | "PUBLISHED") =>
    buildUseCasePayload(formData, status, contributorAlias, contributorDisplayName || contributorAlias);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const { addErrorBanner } = useMessageBannersBox();
  const catalogicalService = useMemo(() => new CatalogicalService<any>("userusecase"), []);
  const csrfService = useMemo(() => new CSRFService(), []);

  const handleSaveDraft = async () => {
    const payload = buildPayload("DRAFT");
    try {
      const csrfToken = await csrfService.getToken();
      if (draftId && savedItem) {
        await catalogicalService.updateUserUseCase(draftId, { ...savedItem, ...payload }, csrfToken);
      } else {
        const result = await catalogicalService.submitUserUseCase(payload, csrfToken);
        if (result && !("errorMessage" in result)) {
          onDraftSaved?.(result.item?.taxonomyId, result.item);
        }
      }
    } catch {
      // Draft save is best-effort
      if (process.env.NODE_ENV === "development") {
        console.log("[UseCasesExperience] Draft payload:", JSON.stringify(payload, null, 2));
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    const payload = buildPayload("PUBLISHED");

    try {
      const csrfToken = await csrfService.getToken();
      let result;
      if (draftId && savedItem) {
        result = await catalogicalService.updateUserUseCase(draftId, { ...savedItem, ...payload }, csrfToken);
      } else {
        result = await catalogicalService.submitUserUseCase(payload, csrfToken);
      }
      if (result && "errorMessage" in result) {
        addErrorBanner(result.errorMessage || "Failed to submit use case", false);
        setIsSubmitting(false);
        return;
      }
      setIsSubmitting(false);
      setIsSubmitted(true);
    } catch (err: any) {
      addErrorBanner(err.message || "Failed to submit use case", false);
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <PageMainContainer>
        <Col alignItems="center" gridGap="S400" padding={{ top: "S600", bottom: "S600" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "#1DAF5C",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text fontSize="T400" color="white" fontWeight="bold">
              ✓
            </Text>
          </div>

          <Text fontSize="T500" fontWeight="bold">
            {getString("useCases.contributeForm.submittedTitle")}
          </Text>
          <Text fontSize="T200" color="var(--st-dt-color-text-secondary)" textAlign="center">
            {getString("useCases.contributeForm.submittedDescription")}
          </Text>

          <Col
            padding="S400"
            style={{
              border: "1px solid var(--st-dt-color-border-primary)",
              borderRadius: 8,
              width: "100%",
              maxWidth: 600,
            }}
            gridGap="S200"
          >
            <Row justifyContent="space-between">
              <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
                {getString("useCases.contributeForm.submissionSummary")}
              </Text>
              <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
                {formData.categories.map(categoryLabel).join(", ")}
              </Text>
            </Row>
            <Text fontSize="T300" fontWeight="bold">
              {formData.title}
            </Text>
            <Text
              fontSize="T200"
              color="var(--st-dt-color-text-secondary)"
              style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
              {formData.shortDescription}
            </Text>
            <Row gridGap="S300" alignItems="center" justifyContent="space-between">
              <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
                {getString("useCases.contributeForm.submittedBy", { alias: contributorAlias })}
              </Text>
              <Text fontSize="T100" color="var(--st-dt-color-text-secondary)">
                {today}
              </Text>
            </Row>
          </Col>

          <Row gridGap="S300" padding={{ top: "S300" }}>
            <Button
              variant={ButtonVariant.Primary}
              onClick={() => history.push("/aiforme/usecases")}
              touchpoint="use-case-view-community"
            >
              {getString("useCases.contributeForm.viewCommunity")}
            </Button>
            <Button variant={ButtonVariant.Secondary} onClick={onSubmitted} touchpoint="use-case-contribute-another">
              {getString("useCases.contributeForm.contributeAnother")}
            </Button>
          </Row>
        </Col>
      </PageMainContainer>
    );
  }

  // Build data for the detail page preview
  const steps = formData.steps
    .filter((s) => s.title.trim())
    .map((s) => ({
      title: s.title,
      expectedResult: s.expectedResult,
      codeSnippet: s.codeSnippet,
      prompt: s.prompt,
      learning: s.learning,
    }));

  const toolDisplayNames = formData.toolsUsed.map(
    (id) => toolNames[id] || id.split("/").pop()?.replace(/-/g, " ") || id
  );

  return (
    <>
      <PageMainContainer>
        <Col padding={{ top: "S400", bottom: "S400" }}>
          <ProgressTracker
            selectedStep={CONTRIBUTE_STEPS.length - 1}
            onStepSelected={() => {}}
            aria-label="Use case contribution progress"
          >
            {CONTRIBUTE_STEPS.map((label, index) => (
              <ProgressTrackerStep
                key={label}
                labelText={label}
                status={
                  index < CONTRIBUTE_STEPS.length - 1
                    ? ProgressTrackerStepStatus.Complete
                    : ProgressTrackerStepStatus.Current
                }
              />
            ))}
          </ProgressTracker>
        </Col>

        <Col padding={{ top: "dimensions.spacing.400", bottom: "dimensions.spacing.600" }} width="100%">
          <Col gridGap="dimensions.spacing.400" padding={{ top: "dimensions.spacing.300" }} width="100%">
            {/* Action buttons + cancel X (same row) */}
            <Row justifyContent="space-between" alignItems="center">
              <Row gridGap="dimensions.spacing.200">
                <Button variant={ButtonVariant.Secondary} onClick={onBack} touchpoint="use-case-review-back">
                  {getString("useCases.contributeForm.back")}
                </Button>
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  touchpoint="use-case-submit"
                >
                  {isSubmitting
                    ? getString("useCases.contributeForm.submitting")
                    : getString("useCases.contributeForm.submit")}
                </Button>
                <Button
                  variant={ButtonVariant.Tertiary}
                  onClick={handleSaveDraft}
                  touchpoint="use-case-review-save-draft"
                >
                  {getString("useCases.contributeForm.saveDraft")}
                </Button>
              </Row>
              <Button
                variant={ButtonVariant.Tertiary}
                icon={<IconCross />}
                aria-label="Cancel"
                touchpoint="use-case-review-cancel"
                onClick={() => history.goBack()}
              />
            </Row>

            {/* Title */}
            <H2>{formData.title}</H2>

            {/* Category chips */}
            <Row gridGap="dimensions.spacing.200" alignItems="center">
              {formData.categories.map((cat) => (
                <Chip key={cat} backgroundColor={ChipBackgroundColor.Blue}>
                  {categoryLabel(cat)}
                </Chip>
              ))}
              {formData.difficultyLevel && (
                <Chip backgroundColor={ChipBackgroundColor.Default}>{formData.difficultyLevel}</Chip>
              )}
            </Row>

            <Text fontSize="T200">{formData.shortDescription}</Text>

            {/* Contributor + tools + audience (no comment/used counts) */}
            <UseCaseMetaBar
              contributorAlias={contributorAlias}
              date={today}
              tools={toolDisplayNames}
              recommendedAudience={formData.targetAudience}
            />

            {/* Impact */}
            <H3>{getString("useCases.detail.impact")}</H3>
            <UseCaseImpactPanel
              impact={{
                before: formData.beforeWorkflow,
                after: formData.afterWorkflow,
                keyImprovements: impactIndicators.length > 0 ? impactIndicators : undefined,
              }}
            />

            {/* Steps */}
            {steps.length > 0 && (
              <>
                <H3>{getString("useCases.detail.steps")}</H3>
                <UseCaseSteps steps={steps} />
              </>
            )}

            {/* Prerequisites */}
            {formData.prerequisites.filter(Boolean).length > 0 && (
              <>
                <Spacer height="dimensions.spacing.200" />
                <H3>{getString("useCases.detail.prerequisites")}</H3>
                <Col gridGap="dimensions.spacing.200">
                  {formData.prerequisites.filter(Boolean).map((p, i) => (
                    <StatusIndicator key={i} status={Status.Positive} messageText={p} />
                  ))}
                </Col>
              </>
            )}

            {/* Learn more */}
            {formData.learnMoreLinks.some((l) => l.url.trim()) && (
              <>
                <Spacer height="dimensions.spacing.200" />
                <H3>{getString("useCases.detail.learnMore")}</H3>
                <Col gridGap="dimensions.spacing.200">
                  {formData.learnMoreLinks
                    .filter((l) => l.url.trim())
                    .map((link, i) => (
                      <Row key={i} gridGap="dimensions.spacing.100" alignItems="center">
                        <Link href={link.url} target="_blank" touchpoint="use-case-review-link">
                          {link.title || link.url}
                        </Link>
                        <IconExternalLinkSmall aria-hidden="true" />
                      </Row>
                    ))}
                </Col>
              </>
            )}
          </Col>
        </Col>
      </PageMainContainer>
    </>
  );
};
